<!--
  ForgotPasswordModal.vue
  Modal for requesting password reset email

  Emits:
  - close: when modal should close
-->

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useProfile } from '@/composables/useProfile'

defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { requestPasswordReset, loading } = useProfile()

// Form state
const email = ref('')
const submitted = ref(false)
const errorMessage = ref('')

// Validation
const emailError = computed(() => {
  if (!email.value) return ''
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailPattern.test(email.value) ? '' : 'Invalid email format'
})

const isFormValid = computed(() => {
  return email.value && !emailError.value
})

async function handleSubmit() {
  if (!isFormValid.value) return

  errorMessage.value = ''

  try {
    await requestPasswordReset(email.value)
    submitted.value = true
  } catch (err: unknown) {
    // Even on error, show success to prevent email enumeration
    submitted.value = true
  }
}

function handleClose() {
  // Reset state
  email.value = ''
  submitted.value = false
  errorMessage.value = ''
  emit('close')
}

function handleBackdropClick(event: MouseEvent) {
  if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
    handleClose()
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="modal-backdrop"
      @click="handleBackdropClick"
      @keydown.esc="handleClose"
    >
      <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="forgot-title">
        <!-- Header -->
        <div class="modal-header">
          <h2 id="forgot-title">Reset Password</h2>
          <button class="close-btn" @click="handleClose" aria-label="Close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <!-- Content -->
        <div class="modal-content">
          <!-- Success State -->
          <div v-if="submitted" class="success-state">
            <div class="success-icon">
              <span class="material-symbols-outlined">mark_email_read</span>
            </div>
            <h3>Check Your Email</h3>
            <p>
              If an account exists for <strong>{{ email }}</strong>, you'll receive a password
              reset link shortly.
            </p>
            <p class="note">
              Don't see it? Check your spam folder. The link expires in 30 minutes.
            </p>
            <button class="btn-primary" @click="handleClose">Back to Login</button>
          </div>

          <!-- Form State -->
          <template v-else>
            <p class="description">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="reset-email">Email Address</label>
                <input
                  id="reset-email"
                  v-model="email"
                  type="email"
                  required
                  :disabled="loading"
                  autocomplete="email"
                  placeholder="you@example.com"
                />
                <span v-if="emailError" class="error-text">{{ emailError }}</span>
              </div>

              <div v-if="errorMessage" class="error-message">
                {{ errorMessage }}
              </div>

              <div class="form-actions">
                <button type="button" class="btn-secondary" @click="handleClose" :disabled="loading">
                  Cancel
                </button>
                <button type="submit" class="btn-primary" :disabled="!isFormValid || loading">
                  {{ loading ? 'Sending...' : 'Send Reset Link' }}
                </button>
              </div>
            </form>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.modal-container {
  background: linear-gradient(135deg, rgba(13, 31, 45, 0.98) 0%, rgba(26, 47, 63, 0.98) 100%);
  border-radius: 16px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 420px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
}

.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #fff;
}

.modal-content {
  padding: 1.5rem;
}

.description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9375rem;
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  width: 64px;
  height: 64px;
  background: rgba(16, 185, 129, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
}

.success-icon .material-symbols-outlined {
  font-size: 2rem;
  color: #10b981;
}

.success-state h3 {
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
  color: #fff;
}

.success-state p {
  margin: 0 0 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9375rem;
  line-height: 1.5;
}

.success-state .note {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 1.5rem;
}

/* Form */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.form-group input {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(212, 167, 44, 0.5);
  box-shadow: 0 0 0 3px rgba(212, 167, 44, 0.1);
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  margin-bottom: 1rem;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-primary,
.btn-secondary {
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #d4a72c 0%, #b58e1d 100%);
  border: none;
  color: #1a2f3f;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #e5b933 0%, #c99f2e 100%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
